import {useLayoutEffect, useRef} from 'react';

const COLLAPSED_CLASS = 'topbar-collapsed';
const PANEL_OPEN_CLASS = 'list-open';

const HYSTERESIS = 30;

function outerWidth(el) {
  const {marginLeft, marginRight} = window.getComputedStyle(el);
  return el.offsetWidth + (parseFloat(marginLeft) || 0) + (parseFloat(marginRight) || 0);
}

export default function useTopbarOverflow() {
  const rowRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const left = leftRef.current;
    const right = rightRef.current;
    const menu = menuRef.current;

    if (!row || !left || !right || !menu) return;

    const widthNeeded = () =>
      [left, ...right.children].reduce((total, el) => total + outerWidth(el), 0);

    const update = () => {
      const body = document.body;
      const available = row.clientWidth;

      if (!available) return;

      const isCollapsed = body.classList.contains(COLLAPSED_CLASS);
      if (isCollapsed && body.classList.contains(PANEL_OPEN_CLASS)) return;

      const needed = widthNeeded();
      const collapse = isCollapsed
        ? needed + HYSTERESIS > available // Collapsed: reopen only with clear room.
        : needed > available;             // Inline: collapse as soon as it spills.

      body.classList.toggle(COLLAPSED_CLASS, collapse);

      if (!collapse) body.classList.remove(PANEL_OPEN_CLASS);
    };

    let frame = null;
    const scheduleUpdate = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        update();
      });
    };

    update();

    let observer = null;
    if (typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(scheduleUpdate);
      observer.observe(row);
      observer.observe(menu);
    } else {
      window.addEventListener('resize', scheduleUpdate);
    }

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', scheduleUpdate);
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.body.classList.remove(COLLAPSED_CLASS);
    };
  }, []);

  return {rowRef, leftRef, rightRef, menuRef};
}
